import * as THREE from "three";
import type { Vec3 } from "@genjiball/protocol";
import { makeNametag, updateNametag } from "../render/nametag.js";
import { PlayerVisual } from "../render/playerVisual.js";

const CAPSULE_HEIGHT = 1.8;
const GROUNDED_Y_EPS = 0.05;

export class RemotePlayer {
  readonly id: string;
  readonly group: THREE.Group;
  readonly visual: PlayerVisual;
  private nametag: THREE.Sprite;
  private currentName: string;
  private currentColor: string;

  // Snapshot buffer for interpolation.
  // Each entry: { tServer (ms), pos, yaw }.
  private buffer: Array<{ t: number; pos: Vec3; yaw: number }> = [];
  private static readonly INTERP_DELAY_MS = 120;

  // dt tracking for the animation mixer.
  private lastRenderTimeMs: number = 0;
  // Estimated velocity from successive snapshots (snapshots don't carry vel).
  private estVel: [number, number, number] = [0, 0, 0];

  constructor(id: string, name: string, color: string, spawn: Vec3) {
    this.id = id;
    this.currentName = name;
    this.currentColor = color;

    this.group = new THREE.Group();
    this.group.position.set(...spawn);

    this.visual = new PlayerVisual(color);
    this.group.add(this.visual.group);

    this.nametag = makeNametag(name, color);
    this.nametag.position.y = CAPSULE_HEIGHT + 0.5;
    this.group.add(this.nametag);

    // Kick off async character load. The capsule placeholder shows until done.
    void this.visual.loadCharacter(id);
  }

  applyMeta(name: string, color: string) {
    if (name !== this.currentName) {
      updateNametag(this.nametag, name, color);
      this.currentName = name;
    }
    if (color !== this.currentColor) {
      this.visual.setColor(color);
      this.currentColor = color;
    }
  }

  ingestSnapshot(serverTimeMs: number, pos: Vec3, yaw: number) {
    if (this.buffer.length === 0) {
      console.log(`[RemotePlayer] ${this.id} first snapshot pos=[${pos[0].toFixed(2)}, ${pos[1].toFixed(2)}, ${pos[2].toFixed(2)}]`);
    }
    this.buffer.push({ t: serverTimeMs, pos, yaw });
    if (this.buffer.length > 12) this.buffer.shift();
  }

  /** Render the entity at `renderTimeMs - INTERP_DELAY_MS`. */
  update(renderTimeMs: number) {
    const dt = this.lastRenderTimeMs
      ? Math.max(0, (renderTimeMs - this.lastRenderTimeMs) / 1000)
      : 0;
    this.lastRenderTimeMs = renderTimeMs;

    const target = renderTimeMs - RemotePlayer.INTERP_DELAY_MS;
    // Find the two snapshots surrounding target.
    let prev = this.buffer[0];
    let next: typeof prev | undefined;
    for (let i = 1; i < this.buffer.length; i++) {
      const b = this.buffer[i]!;
      if (b.t >= target) {
        next = b;
        prev = this.buffer[i - 1]!;
        break;
      }
    }

    if (!prev) {
      this.visual.update(dt);
      return;
    }

    if (!next) {
      // Past the most recent; snap to prev.
      this.group.position.set(...prev.pos);
      this.group.rotation.y = prev.yaw;
    } else {
      const span = next.t - prev.t;
      const t = span > 0 ? Math.max(0, Math.min(1, (target - prev.t) / span)) : 1;
      this.group.position.set(
        prev.pos[0] + (next.pos[0] - prev.pos[0]) * t,
        prev.pos[1] + (next.pos[1] - prev.pos[1]) * t,
        prev.pos[2] + (next.pos[2] - prev.pos[2]) * t,
      );
      this.group.rotation.y = prev.yaw + (next.yaw - prev.yaw) * t;

      // Estimate velocity from the snapshot delta — feeds the animation state.
      if (span > 0) {
        const inv = 1000 / span;
        this.estVel = [
          (next.pos[0] - prev.pos[0]) * inv,
          (next.pos[1] - prev.pos[1]) * inv,
          (next.pos[2] - prev.pos[2]) * inv,
        ];
      }
    }

    // Drive character animation from inferred state.
    const grounded = this.group.position.y <= GROUNDED_Y_EPS;
    this.visual.applyMovementState({ vel: this.estVel, grounded });
    this.visual.update(dt);
  }

  dispose() {
    this.visual.dispose();
    (this.nametag.material.map as THREE.Texture | null)?.dispose();
    this.nametag.material.dispose();
  }
}
